import { useState, useEffect } from 'react';
import axios from 'axios';
import { SortConfig } from '../components/DataTable';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export interface Simulation {
  id: string;
  createdAt: string;
  platform: string;
  content: string;
  sender: string;
  sector: string;
  hasImage: boolean;
  isPrivate: boolean;
  resultJson: any;
  user?: {
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface PaginationInfo {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface FilterConfig {
  platform?: string;
  eventType?: string;
  hasImage?: boolean;
  isPrivate?: boolean;
  createdAfter?: string;
  createdBefore?: string;
  reputationImpact?: string;
  confidence?: string;
  createdBy?: string;
  account?: string;
  sector?: string;
}

interface UseSimulationsParams {
  page: number;
  rowsPerPage: number;
  sortConfig: SortConfig;
  filterConfig: FilterConfig;
  onTrialApiError?: (error: any) => boolean;
  onSubscriptionApiError?: (error: any) => boolean;
}

interface UseSimulationsReturn {
  simulations: Simulation[];
  loading: boolean;
  error: string | null;
  totalCount: number;
  fetchSimulations: (pageNum?: number, limit?: number, opts?: { silent?: boolean }) => Promise<void>;
  refetch: () => void;
}

export const useSimulations = ({
  page,
  rowsPerPage,
  sortConfig,
  filterConfig,
  onTrialApiError,
  onSubscriptionApiError
}: UseSimulationsParams): UseSimulationsReturn => {
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const fetchSimulations = async (pageNum = page + 1, limit = rowsPerPage, opts?: { silent?: boolean }) => {
    try {
      if (!opts?.silent) setLoading(true);
      setError(null);
      
      // Build params from current state
      const params: any = {
        page: pageNum,
        limit: limit,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
        ...filterConfig
      };
      
      const response = await axios.get(`${API_URL}/api/user/simulations`, { params });
      setSimulations(response.data.simulations);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (err) {
      // Check if trial has expired or subscription is cancelled
      const trialHandled = onTrialApiError?.(err);
      const subscriptionHandled = onSubscriptionApiError?.(err);
      
      if (!trialHandled && !subscriptionHandled) {
        setError('Failed to load simulations. Please try again later.');
        logger.error('Error fetching simulations:', err);
      }
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  };

  const refetch = () => {
    fetchSimulations();
  };

  useEffect(() => {
    fetchSimulations();
  }, [page, rowsPerPage, sortConfig, filterConfig]);

  // Silent polling while there are in-progress rows (no forecast yet)
  useEffect(() => {
    const hasInProgress = simulations.some((s) => !(s as any)?.resultJson?.data?.forecast);
    if (!hasInProgress) return;
    const interval = setInterval(() => {
      fetchSimulations(page + 1, rowsPerPage, { silent: true });
    }, 6000);
    return () => clearInterval(interval);
  }, [simulations, page, rowsPerPage, sortConfig, filterConfig]);

  return {
    simulations,
    loading,
    error,
    totalCount,
    fetchSimulations,
    refetch
  };
};

export default useSimulations;
